import {
  type TerraUIMessage,
  type TerraUIMessagePart,
  type TerraUIMessageDataPart,
  TerraUIMessageDataPartName,
  type HITLDecision,
  type HITLRequestConfirmationDecision,
  type ConfirmationMessageType,
  type DataMessageType,
  type BaseDataMessage,
  DataMessageTypeEnum,
} from '@terra/shared-dto-chat-types';
import { shouldHideLiveToolCall } from './hidden-live-tool-calls';
import {
  suppressCopilotPlanRecapParts,
  suppressPinnedPlanArtifacts,
} from './suppress-copilot-plan-recap';
import { filterSupersededToolCalls } from './superseded-tool-calls';
import { isToolCallData } from './tool-call-data-merge';
import {
  getHITLTargetIds,
  getMetadataAsUserConfirmation,
  isDataOrConfirmationPart,
  isHITLRequestConfirmation,
  isUserConfirmationData,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';
import {
  collectCrossMessagePendingHitlToolCallIds,
  collectPendingHitlToolCallIds,
} from './pending-hitl-tool-call-ids';
import type {
  NeoAttackTrackingPartRef,
  NeoAttackTrackingStateCache,
  PendingAskUserInteraction,
  ProcessedMessage,
  ProcessedPart,
  ProcessedPartKind,
} from './message-processing-types';
import {
  type HITLRequestIndex,
  collectSettledBackgroundTerminalToolCallIds,
  hitlRequestActionToolCallIds,
  hitlRequestTargetsTerminalTool,
  indexHITLRequests,
  isBackgroundChildDataMessage,
  resolveHITLDecision,
  resolveToolCallHITLStatus,
} from './message-processing-hitl';
import {
  chatHasActiveNeoAttackTracking,
  computeNeoAttackTrackingIdentity,
  finalizeStaleStreamDataMessage,
  resolveChatHasActiveNeoAttackTracking,
  resolveNeoAttackTrackingState,
} from './message-processing-neo-tracking';
import {
  buildToolResultsById,
  mergeToolCallPartsAcrossMessages,
} from './message-processing-tool-merge';
import {
  collectAskUserAnswers,
  collectExternalToolResults,
  enrichAskUserToolCalls,
  enrichExternalToolCalls,
} from './message-processing-enrichment';
import {
  TextPartSchema,
  TraceMetadataDataSchema,
  extractPendingAskUserInteraction,
  getPartId,
  hasTextContent,
  isAskUserData,
  resolveVisibleTodos,
} from './message-processing-todos-and-ask-user';

export {
  getHITLTargetIds,
  getMetadataAsUserConfirmation,
  isDataOrConfirmationPart,
  isHITLRequestConfirmation,
  parseBaseDataMessage,
  buildToolResultsById,
  chatHasActiveNeoAttackTracking,
  computeNeoAttackTrackingIdentity,
  resolveChatHasActiveNeoAttackTracking,
  resolveNeoAttackTrackingState,
  extractPendingAskUserInteraction,
  hasTextContent,
};

export type {
  ProcessedPartKind,
  ProcessedPart,
  ProcessedMessage,
  PendingAskUserInteraction,
  NeoAttackTrackingPartRef,
  NeoAttackTrackingStateCache,
};

function findLastAssistantIndex(messages: TerraUIMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return i;
  }

  return -1;
}

function extractTraceId(parts: TerraUIMessage['parts']): string | undefined {
  for (const part of parts) {
    if (!isDataOrConfirmationPart(part) || !part.data) continue;
    const parsed = TraceMetadataDataSchema.safeParse(part.data);

    if (parsed.success) return parsed.data.traceId;
  }

  return undefined;
}

function readParentToolCallId(record: unknown): string | undefined {
  if (!record || typeof record !== 'object') {
    return undefined;
  }

  const value: unknown = Reflect.get(record, 'parentToolCallId');

  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function partOwnerKey(part: TerraUIMessagePart): string {
  if (part.type === 'text' || part.type === 'reasoning') {
    return readParentToolCallId(part.providerMetadata?.metadata) ?? 'main';
  }

  if (isDataOrConfirmationPart(part) && part.data) {
    return readParentToolCallId(Reflect.get(part.data, 'metadata')) ?? 'main';
  }

  return 'main';
}

function processAssistantParts({
  filteredParts,
  uiMessage,
  messages,
  messageIndex,
  messageKey,
  shouldFinalizeStaleToolCalls,
  hasActiveNeoAttackTracking,
  visibleTodosPartIds,
  backUpdatedTodosByPartId,
  seenAssistantTextByMessageId,
  hitlIndex,
  pendingHitlToolCallIds,
  settledBackgroundTerminalToolCallIds,
}: {
  filteredParts: TerraUIMessage['parts'];
  uiMessage: TerraUIMessage;
  messages: TerraUIMessage[];
  messageIndex: number;
  messageKey: string;
  shouldFinalizeStaleToolCalls: boolean;
  hasActiveNeoAttackTracking: boolean;
  visibleTodosPartIds: Set<string>;
  backUpdatedTodosByPartId: Map<string, BaseDataMessage>;
  seenAssistantTextByMessageId: Map<string, Set<string>>;
  hitlIndex: HITLRequestIndex;
  pendingHitlToolCallIds: Set<string>;
  settledBackgroundTerminalToolCallIds: Set<string>;
}): ProcessedPart[] {
  const idlessOrdinals = new Map<string, number>();
  const keyRepeats = new Map<string, number>();

  return filteredParts.map((part) => {
    const dataId = getPartId(part) ?? '';
    const idlessScope = `${part.type}-${partOwnerKey(part)}`;
    const ordinal = idlessOrdinals.get(idlessScope) ?? 0;

    if (!dataId) {
      idlessOrdinals.set(idlessScope, ordinal + 1);
    }

    const baseKey = dataId
      ? `${messageKey}-${part.type}-${dataId}`
      : `${messageKey}-${idlessScope}-${ordinal}`;
    const repeat = keyRepeats.get(baseKey) ?? 0;

    keyRepeats.set(baseKey, repeat + 1);

    const key = repeat > 0 ? `${baseKey}#${repeat}` : baseKey;
    const messagePartId = dataId || undefined;

    if (!isDataOrConfirmationPart(part) || !part.data) {
      const isAssistantTextPart =
        part.type === 'text' || part.type === 'reasoning';

      if (isAssistantTextPart && part.type === 'text') {
        const parsedText = TextPartSchema.safeParse(part);
        const textContent = parsedText.success
          ? parsedText.data.text.trim()
          : '';
        const messageId = uiMessage.id ?? `message-${messageIndex}`;
        const seenTextForMessage =
          seenAssistantTextByMessageId.get(messageId) ?? new Set<string>();

        if (textContent && seenTextForMessage.has(textContent)) {
          return {
            part,
            key,
            kind: 'skip',
            messagePartId,
          } satisfies ProcessedPart;
        }

        if (textContent) {
          seenTextForMessage.add(textContent);
          seenAssistantTextByMessageId.set(messageId, seenTextForMessage);
        }
      }

      return {
        part,
        key,
        kind: isAssistantTextPart ? 'assistant-text' : 'skeleton',
        messagePartId,
      } satisfies ProcessedPart;
    }

    const parsedDataMessage = parseBaseDataMessage(part.data);

    if (!parsedDataMessage) {
      return { part, key, kind: 'skip', messagePartId } satisfies ProcessedPart;
    }

    const rawRenderedDataMessage =
      parsedDataMessage.type === DataMessageTypeEnum.Todos && messagePartId
        ? (backUpdatedTodosByPartId.get(messagePartId) ?? parsedDataMessage)
        : parsedDataMessage;
    const renderedDataMessage = shouldFinalizeStaleToolCalls
      ? finalizeStaleStreamDataMessage(rawRenderedDataMessage, {
          hasActiveNeoAttackTracking,
          pendingHitlToolCallIds,
        })
      : rawRenderedDataMessage;

    if (
      renderedDataMessage.type === DataMessageTypeEnum.TraceMetadata ||
      renderedDataMessage.type === DataMessageTypeEnum.BackgroundUpdate ||
      renderedDataMessage.type === DataMessageTypeEnum.TokenUsage ||
      renderedDataMessage.type === DataMessageTypeEnum.Compaction
    ) {
      return { part, key, kind: 'skip', messagePartId } satisfies ProcessedPart;
    }

    if (
      renderedDataMessage.type === DataMessageTypeEnum.Todos &&
      messagePartId &&
      !visibleTodosPartIds.has(messagePartId)
    ) {
      return { part, key, kind: 'skip', messagePartId } satisfies ProcessedPart;
    }

    if (
      isToolCallData(renderedDataMessage) &&
      shouldHideLiveToolCall({
        toolCallId: renderedDataMessage.toolCallId,
        toolName: renderedDataMessage.toolName,
        metadata: renderedDataMessage.metadata,
        message: renderedDataMessage.message,
        labelPast: renderedDataMessage.labelPast,
        labelPresent: renderedDataMessage.labelPresent,
      })
    ) {
      return { part, key, kind: 'skip', messagePartId } satisfies ProcessedPart;
    }

    if (isAskUserData(renderedDataMessage)) {
      return { part, key, kind: 'skip', messagePartId } satisfies ProcessedPart;
    }

    if (
      isUserConfirmationData(renderedDataMessage) &&
      Array.isArray(renderedDataMessage.subs)
    ) {
      return {
        part,
        key,
        kind: 'handled-confirmation',
        dataMessage: renderedDataMessage,
        messagePartId,
      } satisfies ProcessedPart;
    }

    let hitlDecision: HITLRequestConfirmationDecision | undefined;
    let hitlDecisions: HITLDecision[] | undefined;
    let hasToolResult = false;
    let wasShownInHITL = false;
    let hitlResolvedToolResultInMessages = false;
    let selectedDecision: HITLRequestConfirmationDecision | undefined;
    let selectedDecisions: HITLDecision[] | undefined;

    if (isHITLRequestConfirmation(renderedDataMessage)) {
      const resolved = resolveHITLDecision({
        dataMessage: renderedDataMessage,
        messagePartId,
        messages,
        messageIndex,
      });

      hitlDecision = resolved.hitlDecision;
      hitlDecisions = resolved.hitlDecisions;
      hasToolResult = resolved.hasToolResult;
      selectedDecision = hitlDecision;
      selectedDecisions = hitlDecisions;

      if (
        isBackgroundChildDataMessage(renderedDataMessage) &&
        hitlRequestTargetsTerminalTool(renderedDataMessage) &&
        hitlRequestActionToolCallIds(renderedDataMessage).some((toolCallId) =>
          settledBackgroundTerminalToolCallIds.has(toolCallId),
        )
      ) {
        return {
          part,
          key,
          kind: 'skip',
          messagePartId,
        } satisfies ProcessedPart;
      }
    }

    if (isToolCallData(renderedDataMessage)) {
      const hitlStatusSource = isToolCallData(parsedDataMessage)
        ? parsedDataMessage
        : renderedDataMessage;
      const toolCallStatus = resolveToolCallHITLStatus({
        dataMessage: hitlStatusSource,
        messages,
        messageIndex,
        hitlIndex,
      });

      wasShownInHITL = toolCallStatus.wasShownInHITL;
      hitlResolvedToolResultInMessages =
        toolCallStatus.hitlResolvedToolResultInMessages;
      selectedDecision = toolCallStatus.decision ?? selectedDecision;
    }

    return {
      part,
      key,
      kind: 'data-message',
      dataMessage: renderedDataMessage as
        | DataMessageType
        | ConfirmationMessageType,
      messagePartId,
      hitlDecision,
      hitlDecisions,
      hasToolResult,
      wasShownInHITL,
      hitlResolvedToolResultInMessages,
      selectedDecision,
      selectedDecisions,
    } satisfies ProcessedPart;
  });
}

export function processMessages(
  messages: TerraUIMessage[],
  {
    isLoading = false,
    hasActiveNeoAttackTracking: hasActiveNeoAttackTrackingInput,
    hideInlinePlanAndTodos = false,
  }: {
    isLoading?: boolean;
    hasActiveNeoAttackTracking?: boolean;
    hideInlinePlanAndTodos?: boolean;
  } = {},
): ProcessedMessage[] {
  const lastAssistantIdx = findLastAssistantIndex(messages);
  const hasActiveNeoAttackTracking =
    hasActiveNeoAttackTrackingInput ?? chatHasActiveNeoAttackTracking(messages);
  const { visibleTodosPartIds, backUpdatedTodosByPartId } =
    resolveVisibleTodos(messages);
  const seenAssistantTextByMessageId = new Map<string, Set<string>>();
  const hitlIndex = indexHITLRequests(messages);
  const crossMessagePendingHitlToolCallIds =
    collectCrossMessagePendingHitlToolCallIds(messages);
  const settledBackgroundTerminalToolCallIds =
    collectSettledBackgroundTerminalToolCallIds(messages);
  const occurrencesByMessageId = new Map<string, number>();

  const processedMessages = messages.map((uiMessage, index) => {
    const messageId = uiMessage.id ?? 'message';
    const occurrence = occurrencesByMessageId.get(messageId) ?? 0;

    occurrencesByMessageId.set(messageId, occurrence + 1);
    const isLastMessage = index === messages.length - 1;
    const isLastAssistantMessage = index === lastAssistantIdx;

    if (uiMessage.role === 'user') {
      const isUserStreaming = uiMessage.parts.some(
        (part) => part.type === 'text' && part.state === 'streaming',
      );
      const hasVisibleContent = uiMessage.parts.some(hasTextContent);

      return {
        message: uiMessage,
        index,
        isLastMessage,
        isLastAssistantMessage,
        parts: [],
        isUserStreaming,
        hasVisibleContent,
      };
    }

    const shouldFinalizeStaleToolCalls =
      (!isLoading && !hasActiveNeoAttackTracking) || !isLastAssistantMessage;

    const filteredParts = filterSupersededToolCalls(uiMessage.parts);
    const traceId = extractTraceId(filteredParts);
    const isBackgroundUpdate = filteredParts.some((part) => {
      if (!isDataOrConfirmationPart(part) || !part.data) return false;
      const data = part.data as { type?: string };

      return data.type === DataMessageTypeEnum.BackgroundUpdate;
    });
    const parts = processAssistantParts({
      filteredParts,
      uiMessage,
      messages,
      messageIndex: index,
      messageKey: `${messageId}-${occurrence}`,
      shouldFinalizeStaleToolCalls,
      hasActiveNeoAttackTracking,
      visibleTodosPartIds,
      backUpdatedTodosByPartId,
      seenAssistantTextByMessageId,
      hitlIndex,
      pendingHitlToolCallIds: new Set([
        ...crossMessagePendingHitlToolCallIds,
        ...collectPendingHitlToolCallIds(filteredParts),
      ]),
      settledBackgroundTerminalToolCallIds,
    });

    return {
      message: uiMessage,
      index,
      isLastMessage,
      isLastAssistantMessage,
      traceId,
      parts,
      isUserStreaming: false,
      hasVisibleContent: true,
      isBackgroundUpdate: isBackgroundUpdate || undefined,
    };
  });

  const mergedMessages = mergeToolCallPartsAcrossMessages(processedMessages);

  enrichAskUserToolCalls({
    processedMessages: mergedMessages,
    answersByToolCallId: collectAskUserAnswers(messages),
  });

  enrichExternalToolCalls({
    processedMessages: mergedMessages,
    resultsByToolCallId: collectExternalToolResults(messages),
  });

  const recapSuppressed = suppressCopilotPlanRecapParts(mergedMessages);

  return hideInlinePlanAndTodos
    ? suppressPinnedPlanArtifacts(recapSuppressed)
    : recapSuppressed;
}

export function prepareTraceHistoryMessages(
  messages: TerraUIMessage[],
  { isLoading = false }: { isLoading?: boolean } = {},
): TerraUIMessage[] {
  const processed = processMessages(messages, { isLoading });

  return processed.map((processedMessage) => {
    if (processedMessage.message.role === 'user') {
      return processedMessage.message;
    }

    return {
      ...processedMessage.message,
      parts: processedMessage.parts
        .filter((processedPart) => processedPart.kind !== 'skip')
        .map((processedPart) => {
          if (
            processedPart.kind === 'data-message' &&
            processedPart.dataMessage
          ) {
            const partId =
              processedPart.messagePartId ??
              (typeof processedPart.part === 'object' &&
              processedPart.part !== null &&
              typeof (processedPart.part as { id?: unknown }).id === 'string'
                ? (processedPart.part as { id: string }).id
                : processedPart.key);

            return {
              type: TerraUIMessageDataPartName,
              id: partId,
              data: processedPart.dataMessage,
            } as TerraUIMessageDataPart;
          }

          return processedPart.part;
        }),
    };
  });
}
